import { sql } from "drizzle-orm";
import type { MigrationConfig } from "drizzle-orm/migrator";

import { config } from "@/config";
import { createLogger } from "@/logger";

const log = createLogger("db");

import { workspaceId as activeWorkspaceId, db, indexedDbName, storageInitError, version } from "@/db/db";
import { feed } from "@/db/schema";
import UseStorage from "@/hooks/useStorage";
import { BootstrapEventsRegistry } from "@/models/BootstrapEventsRegistry";
import migrations from "./migrations.json";

const hasWindow = typeof window !== "undefined";
if (hasWindow && config) {
  // @ts-ignore
  window.baseDomain = config?.baseDomain;
}
const migrationHashMap = migrations
  ?.map((m) => m.hash)
  .flat()
  .join("-")
  .concat(`-${version}`);
const storage = UseStorage("local");
const tableMigrationKey = "table-migrations";
const localStorageTableMigration = storage.getLocalStorage(tableMigrationKey);
type DBWithDialectAndSession = typeof db & {
  dialect: {
    migrate: (
      migrations: unknown,
      session: unknown,
      config: Omit<MigrationConfig, "migrationsFolder">,
    ) => Promise<void>;
  };
  session: unknown;
};

const dbWithExtras = db as DBWithDialectAndSession;

const deleteInvalidDatabase = async (dbName: string) => {
  const closeDb = async () => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName);
      request.onsuccess = (event: any) => {
        const db = event.target.result;
        db.onversionchange = (change) => {
          change.target.close();
          resolve("DB Closed");
        };
        db?.close();

        const deleteRequest = indexedDB.deleteDatabase(dbName);

        deleteRequest.onsuccess = function () {
          log.debug("Database deleted successfully");
          resolve("Success");
        };

        deleteRequest.onerror = function (event) {
          console.error("Error deleting database:", event);
          reject("Error");
        };

        deleteRequest.onblocked = function () {
          console.warn("Delete blocked. Please close all other tabs using this database.");
          reject("Blocked");
        };

        resolve("DB Closed");
      };
      request.onerror = function (event) {
        console.error("Error deleting database:", event);
        reject("Close Error");
      };
    });
  };

  return await closeDb();
};

/**
 * Check if the current migration matches what the user has in localStorage.
 * If it doesn't match, delete the IndexedDB and set the new migration hash in localStorage.
 * This is to ensure that the user has the latest migration applied.
 *
 * Skip these top-level steps when storage is unavailable - main.tsx renders
 * the StorageError UI in that case and we must not touch IndexedDB or trigger
 * a reload loop here.
 */
if (storageInitError) {
  log.warn("[Migrations] Skipping migration setup: storage unavailable", { reason: storageInitError.reason });
} else if (!localStorageTableMigration) {
  // New DB initialization: clear bootstrap registry for this workspace
  await BootstrapEventsRegistry.getInstance().clearWorkspace(activeWorkspaceId);
  storage.setLocalStorage({ key: tableMigrationKey, value: migrationHashMap });
  window.location.reload();
} else if (localStorageTableMigration !== migrationHashMap) {
  log.debug("[Migrations] Deleting invalid DB");
  // Migration mismatch: clear bootstrap registry for this workspace
  await BootstrapEventsRegistry.getInstance().clearWorkspace(activeWorkspaceId);
  await deleteInvalidDatabase(indexedDbName);
  storage.setLocalStorage({ key: tableMigrationKey, value: migrationHashMap });
} else {
  log.debug("[Migrations] Valid DB");
}

export async function migrate() {
  if (storageInitError) {
    log.warn("[Migrations] Skipping migrate(): storage unavailable", { reason: storageInitError.reason });
    return;
  }

  // only migrate if you need to
  await dbWithExtras.dialect.migrate(migrations, dbWithExtras.session, {
    migrationsTable: "drizzle_migrations",
  } satisfies Omit<MigrationConfig, "migrationsFolder">);

  // Repair feeds written with -infinity from legacy GREATEST/VALUES sentinel (invalid in JS Date).
  await db.execute(sql`
    UPDATE ${feed}
    SET "latestAliasActivity" = NULL
    WHERE "latestAliasActivity" = '-infinity'::timestamptz
  `);
  await db.execute(sql`
    UPDATE ${feed}
    SET "latestActivity" = NULL
    WHERE "latestActivity" = '-infinity'::timestamptz
  `);
}
